<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import MarkdownIt from 'markdown-it'
import { Icon } from '@easybell/caramel-vue3'
import { getBaseUrlForApi } from '@/utils/api'
import { toLocale } from '@/utils/locale'
import { buildMcpPrompt } from './prompt'
import { useMcpChatStore, type McpChatStepKind } from '@/stores/mcpChat/store'
import { useExtensionsStore } from '@/stores/extensions/store'
import { useDevicesStore } from '@/stores/devices/store'
import { storage } from '@/utils/storage/storage'

const mcpChatStore = useMcpChatStore()
const { t } = useI18n()
const extensionsStore = useExtensionsStore()
const devicesStore = useDevicesStore()
const { locale } = useI18n()

/**
 * Markdown renderer for the chat bubbles. Raw HTML is disabled (`html:
 * false`), so model output is always escaped before formatting and cannot
 * inject markup. Full block rendering (not `renderInline`) is required so
 * tables, lists, headings, and rules become real elements instead of their
 * literal source characters (e.g. a stray line of dashes for `---`).
 */
const markdown = new MarkdownIt({ html: false, linkify: true, breaks: true })

/**
 * Tables are wrapped in a scrollable container instead of letting the table
 * itself compress: squeezing every column to fit the bubble's fixed width
 * forces even short words like "confirmed" to break mid-word.
 */
markdown.renderer.rules.table_open = () => '<div class="mcp-chat-markdown-table"><table>'
markdown.renderer.rules.table_close = () => '</table></div>'

/** Render a step's text as safe Markdown (tables, lists, bold, code, links, ...). */
function renderMarkdown(text: string): string {
  return markdown.render(text)
}

/**
 * Base URL of the AI demo orchestrator. By default the in-process backend
 * endpoint (`/api/ai-demo`) is used so the OpenAI key stays server-side and no
 * separate Node service is required. `VITE_AI_DEMO_URL` can override this to
 * point at a standalone orchestrator during local development.
 */
const AI_DEMO_URL =
  (import.meta.env.VITE_AI_DEMO_URL as string | undefined) ??
  `${getBaseUrlForApi()}ai-demo`

const defaultPrompt = computed(() => t('aiAssistant.example'))
const prompt = ref(defaultPrompt.value)
const overlay = ref<HTMLElement | null>(null)
const stepsContainer = ref<HTMLElement | null>(null)
const textarea = ref<HTMLTextAreaElement | null>(null)
const errorMessage = ref('')
const hasClearedExample = ref(false)

/**
 * The agent's current intermediate thought (plan / narration). It is shown
 * transiently while the agent works and cleared once the final answer arrives,
 * mirroring the live status line of the VS Code AI agent.
 */
const liveStatus = ref('')

/**
 * Friendly placeholder phrases shown while the agent is busy but has not yet
 * emitted a plan/narration (the initial wait and the gaps between steps). They
 * rotate so the live bubble feels alive instead of a static "Working ...".
 */
const THINKING_PHRASES = [
  'aiAssistant.thinking',
  'aiAssistant.working',
  'aiAssistant.nextStep',
  'aiAssistant.checking',
  'aiAssistant.assembling',
  'aiAssistant.lookingUp',
  'aiAssistant.almostThere',
  'aiAssistant.consulting',
  'aiAssistant.calculating',
  'aiAssistant.reviewing',
  'aiAssistant.drafting',
  'aiAssistant.connecting',
] as const

const thinkingIndex = ref(0)
let thinkingTimer: ReturnType<typeof setInterval> | undefined

/** The live bubble text: the agent's own thought, or a rotating placeholder. */
const liveText = computed(
  () => liveStatus.value || t(THINKING_PHRASES[thinkingIndex.value])
)

watch(defaultPrompt, (translated, previous) => {
  if (!hasClearedExample.value && prompt.value === previous) {
    prompt.value = translated
  }
})

watch(
  () => mcpChatStore.isRunning,
  (isRunning) => {
    if (isRunning) {
      thinkingIndex.value = 0
      thinkingTimer = setInterval(() => {
        thinkingIndex.value = (thinkingIndex.value + 1) % THINKING_PHRASES.length
      }, 4000)
    } else if (thinkingTimer) {
      clearInterval(thinkingTimer)
      thinkingTimer = undefined
    }
  }
)

const canSend = computed(
  () => !mcpChatStore.isRunning && prompt.value.trim().length > 0
)

/** Clear the prefilled example the first time the user focuses the input. */
function clearExampleOnFocus(): void {
  if (!hasClearedExample.value && prompt.value === defaultPrompt.value) {
    prompt.value = ''
    nextTick(autoGrow)
  }
  hasClearedExample.value = true
}

/** Grow the textarea with its content, capped by the CSS max-height. */
function autoGrow(): void {
  const element = textarea.value
  if (!element) {
    return
  }
  element.style.height = 'auto'
  element.style.height = `${element.scrollHeight}px`
}

function handlePointerDown(event: PointerEvent): void {
  if (mcpChatStore.isRunning) {
    return
  }
  if (overlay.value && !overlay.value.contains(event.target as Node)) {
    mcpChatStore.close()
  }
}

watch(
  () => mcpChatStore.isOpen,
  (isOpen) => {
    document.body.classList.toggle('mcp-chat-open', isOpen)
    if (isOpen) {
      document.addEventListener('pointerdown', handlePointerDown, true)
      window.addEventListener('resize', autoGrow)
      nextTick(() => {
        textarea.value?.focus()
        autoGrow()
      })
    } else {
      document.removeEventListener('pointerdown', handlePointerDown, true)
      window.removeEventListener('resize', autoGrow)
    }
  }
)

onBeforeUnmount(() => {
  document.body.classList.remove('mcp-chat-open')
  document.removeEventListener('pointerdown', handlePointerDown, true)
  window.removeEventListener('resize', autoGrow)
  if (thinkingTimer) {
    clearInterval(thinkingTimer)
    thinkingTimer = undefined
  }
})

watch(
  () => [mcpChatStore.steps.length, liveStatus.value],
  async () => {
    await nextTick()
    if (stepsContainer.value) {
      stepsContainer.value.scrollTop = stepsContainer.value.scrollHeight
    }
  }
)

type StepIcon =
  | 'User'
  | 'LampSpark'
  | 'Settings'
  | 'CheckmarkInCircle'
  | 'ChatBubble'
  | 'CheckmarkInCircleFilled'
  | 'WarningInTriangle'
  | 'InfoInCircle'

function iconForKind(kind: McpChatStepKind): StepIcon {
  switch (kind) {
    case 'user':
      return 'User'
    case 'plan':
      return 'LampSpark'
    case 'tool_call_start':
      return 'Settings'
    case 'tool_call_result':
      return 'CheckmarkInCircle'
    case 'narration':
      return 'ChatBubble'
    case 'done':
      return 'CheckmarkInCircleFilled'
    case 'error':
      return 'WarningInTriangle'
    default:
      return 'InfoInCircle'
  }
}

/** Read-only MCP tools never change data, so they must not trigger a reload. */
function isReadOnlyTool(tool: string): boolean {
  return /^(list|get|read|fetch|find|search|show)_/.test(tool)
}

/**
 * Refresh every live store and flash the matching UI areas after any
 * data-changing tool call. Read-only tools (list_/get_/...) are ignored, so
 * new write tools work automatically without maintaining a per-tool mapping.
 */
async function applySideEffects(tool: string): Promise<void> {
  if (isReadOnlyTool(tool)) {
    return
  }

  mcpChatStore.startSync()
  try {
    await Promise.all([
      extensionsStore.load({ useCache: false }),
      devicesStore.load({ useCache: false }),
    ])
  } finally {
    mcpChatStore.stopSync()
  }

  mcpChatStore.triggerFlash('users')
  mcpChatStore.triggerFlash('extensions')
  mcpChatStore.triggerFlash('devices')
}

type SseEvent = { event: string; data: Record<string, unknown> }

function parseSseBlock(block: string): SseEvent | null {
  let event = 'message'
  const dataLines: string[] = []

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim())
    }
  }

  if (dataLines.length === 0) {
    return null
  }

  try {
    return { event, data: JSON.parse(dataLines.join('\n')) }
  } catch {
    return null
  }
}

async function handleEvent({ event, data }: SseEvent): Promise<void> {
  switch (event) {
    case 'plan':
      // Transient: shown live, then replaced by the next thought or cleared.
      liveStatus.value = String(data.text ?? '')
      break
    case 'tool_call_start':
      // The narration already explains what happens in plain language, so we
      // do not surface raw tool names (e.g. "Reading modifiable pbxes").
      break
    case 'tool_call_result': {
      const tool = String(data.tool ?? '')
      const ok = data.ok !== false
      // Refresh the affected stores and flash the UI on success. Failures are
      // surfaced by the final result, so we keep the transcript quiet here.
      if (ok) {
        await applySideEffects(tool)
      }
      break
    }
    case 'narration':
      // Transient: shown live, then replaced by the next thought or cleared.
      liveStatus.value = String(data.text ?? '')
      break
    case 'done':
      liveStatus.value = ''
      mcpChatStore.addStep({
        kind: 'done',
        text: String(data.summary ?? t('aiAssistant.done')) || t('aiAssistant.done'),
      })
      break
    case 'error':
      liveStatus.value = ''
      mcpChatStore.addStep({
        kind: 'error',
        text: String(data.message ?? t('aiAssistant.unknownError')),
      })
      break
    default:
      break
  }
}

type ChatMessage = { role: 'user' | 'assistant'; content: string }

/**
 * Build the conversation history from the transcript so the agent can answer
 * follow-ups like "undo the last action". Only the user's prompts and the
 * assistant's final answers are included; intermediate plan/tool steps are
 * left out to keep the context small and relevant.
 */
function buildHistory(): ChatMessage[] {
  return mcpChatStore.steps
    .filter((step) => step.kind === 'user' || step.kind === 'done')
    .map((step) => ({
      role: step.kind === 'user' ? 'user' : 'assistant',
      content: step.text,
    }))
}

async function send(): Promise<void> {
  if (!canSend.value) {
    return
  }

  errorMessage.value = ''
  mcpChatStore.start()
  liveStatus.value = ''

  const token = storage.easybellDeAuth

  // Snapshot the prior conversation (user prompts and final answers) so the
  // agent has context for follow-ups like "undo the last action". Taken before
  // the new message is appended so it is not duplicated.
  const history = buildHistory()

  // Echo the user's message into the transcript and clear the input so it is
  // clear which request the assistant's answers belong to.
  const message = prompt.value.trim()
  mcpChatStore.addStep({ kind: 'user', text: message })
  prompt.value = ''
  nextTick(autoGrow)

  try {
    const response = await fetch(`${AI_DEMO_URL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        prompt: buildMcpPrompt(message, toLocale(locale.value)),
        history,
      }),
    })

    if (!response.ok || !response.body) {
      throw new Error(t('aiAssistant.serverError', { status: response.status }))
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    for (;;) {
      const { value, done } = await reader.read()
      if (done) {
        break
      }

      buffer += decoder.decode(value, { stream: true })

      let separatorIndex = buffer.indexOf('\n\n')
      while (separatorIndex !== -1) {
        const block = buffer.slice(0, separatorIndex)
        buffer = buffer.slice(separatorIndex + 2)

        const parsed = parseSseBlock(block)
        if (parsed) {
          await handleEvent(parsed)
        }

        separatorIndex = buffer.indexOf('\n\n')
      }
    }
  } catch (error) {
    errorMessage.value =
      error instanceof Error ? error.message : t('aiAssistant.connectionFailed')
    mcpChatStore.addStep({ kind: 'error', text: errorMessage.value })
  } finally {
    liveStatus.value = ''
    mcpChatStore.finish()
  }
}
</script>

<template>
  <Teleport to="body">
  <!-- Floating trigger sits exactly where the prompt appears (bottom-center),
       so it can morph straight into the input dock -->
  <transition name="morph-trigger">
    <button
      v-if="!mcpChatStore.isOpen"
      type="button"
      class="liquid-trigger fixed bottom-6 left-1/2 z-[110]"
      data-test="mcp-chat-toggle"
      :aria-label="t('aiAssistant.open')"
      @click="mcpChatStore.toggle()"
    >
      <span class="liquid-glass-effect"></span>
      <span class="liquid-glass-tint"></span>
      <span class="liquid-glass-shine"></span>
      <span class="liquid-glass-content flex items-center gap-2">
        <svg
          class="sparkle-icon size-5"
          viewBox="0 0 24 24"
          fill="url(#sparkle-rainbow)"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <linearGradient
              id="sparkle-rainbow"
              x1="0"
              y1="0"
              x2="24"
              y2="24"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stop-color="#ff2d55" />
              <stop offset="0.4" stop-color="#ff9500" />
              <stop offset="0.7" stop-color="#bf5af2" />
              <stop offset="1" stop-color="#0a84ff" />
            </linearGradient>
          </defs>
          <path
            d="M11.5 14.5c-3.2.55-3.95 1.3-4.5 4.5-.55-3.2-1.3-3.95-4.5-4.5 3.2-.55 3.95-1.3 4.5-4.5.55 3.2 1.3 3.95 4.5 4.5Z"
          />
          <path
            d="M21.5 7.5c-2.4.41-2.96.97-3.37 3.37-.41-2.4-.97-2.96-3.38-3.37 2.41-.41 2.97-.97 3.38-3.37.41 2.4.97 2.96 3.37 3.37Z"
          />
          <path
            d="M20 16.5c-1.6.27-1.98.65-2.25 2.25-.27-1.6-.65-1.98-2.25-2.25 1.6-.27 1.98-.65 2.25-2.25.27 1.6.65 1.98 2.25 2.25Z"
          />
        </svg>
        <span class="liquid-trigger-label">{{ t('aiAssistant.title') }}</span>
      </span>
    </button>
  </transition>

  <!-- Floating dock: only the glowing input is a solid surface; the
       transcript floats above it with a soft shadow so it stays readable -->
  <transition name="morph-dock">
    <section
      v-if="mcpChatStore.isOpen"
      ref="overlay"
      class="mcp-chat-overlay fixed bottom-6 left-1/2 z-[110] flex w-fit min-w-140 max-w-[calc(100vw-3rem)] -translate-x-1/2 flex-col gap-3"
      data-test="mcp-chat-overlay"
    >
      <header class="mcp-chat-header">
        <span>{{ t('aiAssistant.title') }}</span>
        <button
          type="button"
          :aria-label="t('aiAssistant.close')"
          data-test="mcp-chat-close"
          @click="mcpChatStore.close()"
        >
          <Icon name="Close" class="size-5" />
        </button>
      </header>
      <!-- Floating transcript -->
      <div
        ref="stepsContainer"
        class="mcp-chat-steps flex max-h-[calc(100vh-10rem)] flex-col gap-2 overflow-y-auto px-4 py-4"
      >
        <div
          v-for="step in mcpChatStore.steps"
          :key="step.id"
          class="mcp-chat-floating-text flex shrink-0 items-start gap-2 text-sm"
          :class="{
            'mcp-chat-floating-text--user': step.kind === 'user',
            'mcp-chat-floating-text--answer': step.kind === 'done',
            'mcp-chat-floating-text--error':
              step.kind === 'error' || step.ok === false,
            'mcp-chat-floating-text--muted': step.kind === 'tool_call_result',
            'font-medium': step.kind === 'tool_call_start',
          }"
          :data-test="`mcp-chat-step-${step.kind}`"
        >
          <!-- The final answer's checkmark gets a liquid-glass badge to match
               the dock's send button; other steps keep a plain status icon.
               It stays top-aligned so it sits beside the first line of long
               multi-line answers instead of floating in the vertical center. -->
          <span
            v-if="step.kind === 'done'"
            class="mcp-chat-glass-icon shrink-0"
          >
            <Icon name="Checkmark" class="size-3.5" />
          </span>
          <Icon
            v-else
            :name="iconForKind(step.kind)"
            class="mt-0.5 size-4 shrink-0"
          />
          <div class="mcp-chat-markdown" v-html="renderMarkdown(step.text)"></div>
        </div>

        <!-- Live answer bubble: while the agent works this shows its current
             thought, and once done it is replaced in-place by the final
             result step, so the bubble stays where the answer settles -->
        <div
          v-if="mcpChatStore.isRunning"
          class="mcp-chat-floating-text mcp-chat-floating-text--answer mcp-chat-floating-text--live flex shrink-0 items-start gap-2 text-sm"
          data-test="mcp-chat-live-status"
        >
          <span class="mcp-chat-glass-icon shrink-0">
            <Icon name="LampSpark" class="size-3.5" />
          </span>
          <div class="mcp-chat-markdown" v-html="renderMarkdown(liveText)"></div>
        </div>
      </div>

      <!-- Liquid-glass input dock -->
      <div class="liquid-dock-outer">
        <div class="liquid-dock">
          <div class="liquid-glass-effect"></div>
          <div class="liquid-glass-tint"></div>
          <div class="liquid-glass-shine"></div>
          <div class="liquid-glass-content flex items-center gap-2 px-5 py-4">
            <textarea
              ref="textarea"
              v-model="prompt"
              :disabled="mcpChatStore.isRunning"
              rows="1"
              class="glow-textarea"
              :placeholder="t('aiAssistant.placeholder')"
              data-test="mcp-chat-input"
              @focus="clearExampleOnFocus()"
              @input="autoGrow()"
              @keydown.enter.exact.prevent="send()"
            />
            <button
              type="button"
              class="glow-send shrink-0"
              :disabled="!canSend"
              :class="{ 'glow-send--busy': mcpChatStore.isRunning }"
              data-test="mcp-chat-send"
              @click="send()"
            >
              <Icon name="ArrowUp" class="size-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  </transition>
  </Teleport>
</template>

<style scoped>
/* Shared liquid-glass layers (used by the trigger and the input dock).
   Kept subtle: the backdrop stays undistorted, only softened and lit at the rim. */
.liquid-glass-effect {
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  overflow: hidden;
  backdrop-filter: blur(4px) saturate(160%);
  -webkit-backdrop-filter: blur(4px) saturate(160%);
}

.liquid-glass-tint {
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  background: rgba(255, 255, 255, 0.55);
}

.liquid-glass-shine {
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: inherit;
  pointer-events: none;
  box-shadow:
    inset 1px 1px 0 0 rgba(255, 255, 255, 0.7),
    inset -1px -1px 0 0 rgba(255, 255, 255, 0.25),
    inset 0 0 0 1px rgba(255, 255, 255, 0.15);
}

.liquid-glass-content {
  position: relative;
  z-index: 3;
}

/* Liquid-glass trigger pill (same look as the input dock) */
.liquid-trigger {
  position: fixed;
  isolation: isolate;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  padding: 0.7rem 1.4rem;
  border-radius: 9999px;
  font-weight: 600;
  color: #0b1220;
  transform: translateX(-50%);
  transform-origin: bottom center;
  box-shadow:
    0 4px 5px rgba(0, 0, 0, 0.16);
  transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 2.2),
    box-shadow 0.2s ease;
}

.liquid-trigger:hover {
  transform: translateX(-50%) translateY(-2px);
  box-shadow:
    0 6px 7px rgba(0, 0, 0, 0.2);
}

/* Floating transcript: each line is a real liquid-glass pill, using the same
   layered effect as the input dock (distortion + tint + specular shine) */
.mcp-chat-floating-text {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  color: #0b1220;
  border-radius: 0.5rem;
  padding: 0.75rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow:
    0 0 18px -2px rgba(15, 23, 42, 0.14),
    0 6px 24px -6px rgba(15, 23, 42, 0.18),
    0 2px 8px -2px rgba(15, 23, 42, 0.12);
}

/* Glass blur + tint. The heavy SVG distortion used on the large dock is left
   out here: on small pills it warps the backdrop into visible brush-like
   waves, so we keep just the clean frosted-glass blur. */
.mcp-chat-floating-text::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(12px) saturate(150%);
  -webkit-backdrop-filter: blur(12px) saturate(150%);
}

/* Specular shine (same as .liquid-glass-shine) */
.mcp-chat-floating-text::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  pointer-events: none;
  box-shadow:
    inset 2px 2px 1px 0 rgba(255, 255, 255, 0.5),
    inset -1px -1px 1px 1px rgba(255, 255, 255, 0.5);
}

/* Keep the icon and text above the glass layers */
.mcp-chat-floating-text > * {
  position: relative;
  z-index: 2;
}

/* Liquid-glass badge for the final answer's checkmark, mirroring the dock's
   send button (frosted blur + inset specular highlights). */
.mcp-chat-glass-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;
  color: #0b1220;
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(8px) saturate(150%);
  -webkit-backdrop-filter: blur(8px) saturate(150%);
  box-shadow:
    inset 2px 2px 1px 0 rgba(255, 255, 255, 0.6),
    inset -1px -1px 1px 1px rgba(255, 255, 255, 0.5),
    0 2px 6px rgba(15, 23, 42, 0.18);
}

/* Markdown rendered inside the bubbles (paragraphs, lists, tables, code, links, ...).
   The content is injected via v-html, so it never gets Vue's scoping attribute -
   every descendant selector below needs :deep() to actually match it. */
.mcp-chat-markdown {
  min-width: 0;
  word-break: break-word;
}

.mcp-chat-markdown :deep(> :first-child) {
  margin-top: 0;
}

.mcp-chat-markdown :deep(> :last-child) {
  margin-bottom: 0;
}

.mcp-chat-markdown :deep(p) {
  margin: 0.5rem 0;
}

.mcp-chat-markdown :deep(ul),
.mcp-chat-markdown :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.25rem;
}

.mcp-chat-markdown :deep(h1),
.mcp-chat-markdown :deep(h2),
.mcp-chat-markdown :deep(h3) {
  margin: 0.75rem 0 0.35rem;
  font-weight: 600;
}

.mcp-chat-markdown :deep(h1) {
  font-size: 1.1rem;
}

.mcp-chat-markdown :deep(h2) {
  font-size: 1.05rem;
}

.mcp-chat-markdown :deep(h3) {
  font-size: 1rem;
}

.mcp-chat-markdown :deep(hr) {
  margin: 0.6rem 0;
  border: none;
  border-top: 1px solid rgba(15, 23, 42, 0.15);
}

.mcp-chat-markdown :deep(blockquote) {
  margin: 0.5rem 0;
  padding-left: 0.65rem;
  border-left: 3px solid rgba(15, 23, 42, 0.2);
  color: #475569;
}

.mcp-chat-markdown :deep(pre) {
  margin: 0.5rem 0;
  padding: 0.6rem 0.75rem;
  border-radius: 0.35rem;
  background: rgba(15, 23, 42, 0.08);
  overflow-x: auto;
}

.mcp-chat-markdown :deep(pre code) {
  padding: 0;
  background: transparent;
}

.mcp-chat-markdown :deep(table) {
  border-collapse: collapse;
}

.mcp-chat-markdown :deep(.mcp-chat-markdown-table) {
  margin: 0.5rem 0;
  overflow-x: auto;
}

.mcp-chat-markdown :deep(.mcp-chat-markdown-table table) {
  margin: 0;
}

.mcp-chat-markdown :deep(th),
.mcp-chat-markdown :deep(td) {
  padding: 0.5rem 1rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.18);
  text-align: left !important;
  vertical-align: top !important;
  white-space: nowrap;
}

.mcp-chat-markdown :deep(th + th),
.mcp-chat-markdown :deep(td + td) {
  border-left: 1px solid rgba(15, 23, 42, 0.12);
}

.mcp-chat-markdown :deep(th) {
  border-bottom: 2px solid rgba(15, 23, 42, 0.3);
  background: rgba(15, 23, 42, 0.06);
  font-weight: 600;
}

.mcp-chat-markdown :deep(tbody tr:nth-child(even) td) {
  background: rgba(15, 23, 42, 0.035);
}

/* In the final-answer bubble the checkmark sits in a 1.5rem glass badge. Match
   the text's line box to that height so a single line is vertically centered on
   the badge, while longer answers still start neatly at the top. */
.mcp-chat-floating-text--answer .mcp-chat-markdown {
  line-height: 1.5rem;
}

.mcp-chat-markdown :deep(strong) {
  font-weight: 600;
}

.mcp-chat-markdown :deep(em) {
  font-style: italic;
}

.mcp-chat-markdown :deep(code) {
  padding: 0.05rem 0.3rem;
  border-radius: 0.25rem;
  background: rgba(15, 23, 42, 0.08);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85em;
}

.mcp-chat-markdown :deep(a) {
  color: #0a84ff;
  text-decoration: underline;
}

.mcp-chat-floating-text--muted {
  color: #475569;
}

.mcp-chat-floating-text--error {
  color: #b91c1c;
}

/* The user's own message, set apart and aligned to the right */
.mcp-chat-floating-text--user {
  align-self: flex-end;
  font-weight: 500;
  color: #334155;
  border-color: rgba(100, 116, 139, 0.22);
}

.mcp-chat-floating-text--user::before {
  background: rgba(255, 255, 255, 0.8);
}

/* The assistant's answer, with a subtle brand-gradient tint */
.mcp-chat-floating-text--answer {
  color: #0b1220;
  border-color: rgba(20, 176, 199, 0.3);
}

.mcp-chat-floating-text--answer::before {
  background:
    linear-gradient(
      135deg,
      rgba(236, 0, 110, 0.22),
      rgba(247, 148, 30, 0.2) 30%,
      rgba(148, 74, 172, 0.22) 65%,
      rgba(20, 176, 199, 0.22)
    ),
    rgba(255, 255, 255, 0.7);
}

/* While the agent works, the answer bubble shows its live thought and gently
   pulses; once done, the final result step takes its place in the transcript */
.mcp-chat-floating-text--live span {
  animation: live-status-pulse 1.6s ease-in-out infinite;
}

/* The iridescent tint slowly drifts sideways while the agent is working,
   giving a subtle sense of ongoing progress. */
.mcp-chat-floating-text--live::before {
  background:
    linear-gradient(
      120deg,
      rgba(255, 45, 85, 0.18),
      rgba(255, 149, 0, 0.16) 20%,
      rgba(191, 90, 242, 0.18) 45%,
      rgba(10, 132, 255, 0.18) 70%,
      rgba(100, 210, 255, 0.18) 90%,
      rgba(255, 45, 85, 0.18)
    ),
    rgba(255, 255, 255, 0.7);
  background-size: 250% 100%;
  animation: live-rainbow-drift 4s linear infinite;
}

@keyframes live-rainbow-drift {
  to {
    background-position: 250% 0;
  }
}

@keyframes live-status-pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mcp-chat-floating-text--live span {
    animation: none;
    opacity: 0.85;
  }

  .mcp-chat-floating-text--live::before {
    animation: none;
  }
}

/* Liquid-glass input dock */
.liquid-dock-outer {
  position: relative;
  border-radius: 0.5rem;
}

/* Soft neutral aura beneath the glass, with a gentle brand-orange warmth */
.liquid-dock-outer::before {
  content: '';
  position: absolute;
  inset: -6px;
  z-index: -2;
  border-radius: calc(0.5rem + 6px);
  background: rgba(15, 23, 42, 0.12);
  filter: blur(22px);
  opacity: 0.5;
}

.liquid-dock {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-radius: 0.5rem;
  box-shadow:
    0 6px 6px rgba(0, 0, 0, 0.2),
    0 0 20px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 2.2);
}

.glow-textarea {
  width: 100%;
  resize: none;
  border: none;
  background: transparent;
  color: #0b1220;
  font-size: 0.875rem;
  line-height: 1.5;
  outline: none;
  max-height: 30vh;
  overflow-y: auto;
}

.glow-textarea::placeholder {
  color: rgba(11, 18, 32, 0.45);
}

.glow-send {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 9999px;
  color: #0b1220;
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(8px) saturate(150%);
  -webkit-backdrop-filter: blur(8px) saturate(150%);
  box-shadow:
    inset 2px 2px 1px 0 rgba(255, 255, 255, 0.6),
    inset -1px -1px 1px 1px rgba(255, 255, 255, 0.5),
    0 2px 6px rgba(15, 23, 42, 0.18);
  transition: filter 0.15s ease, opacity 0.15s ease;
}

.glow-send:hover:not(:disabled) {
  filter: brightness(1.08);
}

.glow-send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.glow-send--busy {
  animation: glow-pulse 1.2s ease-in-out infinite;
}

@keyframes glow-pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Morph: the trigger expands away while the dock grows in from the
   same bottom-center anchor, reading as a single morphing surface */
.mcp-chat-overlay {
  transform-origin: bottom center;
}

.morph-trigger-enter-active {
  transition:
    opacity 0.25s ease,
    transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.morph-trigger-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.morph-trigger-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(6px) scale(0.9);
}

.morph-trigger-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-6px) scale(1.18);
}

.morph-dock-enter-active {
  transition:
    opacity 0.35s ease,
    transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

.morph-dock-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.25s ease;
}

.morph-dock-enter-from {
  opacity: 0;
  /* -translate-x-1/2 centers the dock via Tailwind's own `translate` property, so this transform must not repeat it */
  transform: translateY(10px) scale(0.55);
}

.morph-dock-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.85);
}

.mcp-chat-header {
  display: none;
}

@media (max-width: 767px) {
  .liquid-trigger {
    left: auto;
    right: max(1rem, env(safe-area-inset-right));
    bottom: max(1rem, env(safe-area-inset-bottom));
    width: 3.5rem;
    height: 3.5rem;
    padding: 0;
    justify-content: center;
    transform: none;
  }

  .liquid-trigger:hover {
    transform: translateY(-2px);
  }

  .liquid-trigger-label {
    display: none;
  }

  .mcp-chat-overlay {
    inset: 0;
    width: 100%;
    min-width: 0;
    max-width: none;
    height: 100dvh;
    padding: env(safe-area-inset-top) max(1rem, env(safe-area-inset-right))
      env(safe-area-inset-bottom) max(1rem, env(safe-area-inset-left));
    gap: 0;
    background: #fff;
    translate: none;
    transform: none;
  }

  .mcp-chat-header {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: space-between;
    min-height: 3.5rem;
    font-weight: 600;
  }

  .mcp-chat-header button {
    display: flex;
    width: 2.75rem;
    height: 2.75rem;
    align-items: center;
    justify-content: center;
  }

  .mcp-chat-steps {
    flex: 1;
    min-height: 0;
    max-height: none;
    padding-inline: 1rem;
  }

  .mcp-chat-floating-text {
    max-width: 100%;
    min-width: 0;
  }

  .mcp-chat-markdown {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .liquid-dock-outer {
    flex: none;
    margin: 0 0 0.75rem;
  }

  .liquid-dock .liquid-glass-content {
    padding: 0.75rem;
  }

  .glow-textarea {
    min-width: 0;
  }

  .morph-trigger-enter-from,
  .morph-trigger-leave-to,
  .morph-dock-enter-from,
  .morph-dock-leave-to {
    transform: scale(0.95);
  }
}
</style>

<style>
/* SurveyMonkey injects its feedback button into <body> with z-index 999997. */
body.mcp-chat-open .smcx-widget.smcx-passive-feedback {
  display: none !important;
}
</style>
